import { codeUnitString } from "../code-unit-string.js";
import { appLogsQuerySchema } from "../app-logs.js";
import { appFleetLogsQuerySchema } from "../app-fleet-logs.js";
import { cpAuditJsonByteLength } from "./changes.js";
import { singleLineText } from "../single-line-text.js";
import { CP_AUDIT_DETAILS_MAX_BYTES } from "./ingest.js";
import { cpPodNameSchema } from "../cp-pods.js";
import { dpLogsInstantSchema, dpLogsLevelSchema, dpLogsNamespaceSchema } from "../dp-logs-query.js";
import { ZION_CLIENT_PATTERN } from "../operator-client-header.js";
import { telemetryAccessIdSchema } from "../telemetry-access.js";
import { z } from "zod";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils.js";
//#region src/cp-audit/read-details.ts
/**
* The `details` object of a read-audit row: what a log read, an MCP tool call or a CLI request
* records in `cp_auth.audit_log`.
*
* Plan `2026-10-21-operator-tui`, decision D10, defaults row F3, rulings R-007 and R-008, security
* surface rows "MCP tool arguments", "Log query fields" and "`x-zion-client` header".
*
* Who uses it:
*
* - The read-audit helper in `@zion/cp-audit-client` builds a draft and passes it through
*   {@link fitReadAuditDetails}, which cuts an oversized pod list and parses the result with
*   {@link readAuditDetailsSchema} before the helper calls `CpAuditSink.record`.
* - The log services fill the query fields, the target ids and the result fields: six in
*   site-admin-api (the CP pod log, the DP log query, the app pod log, the app logs, the app status
*   and the app fleet logs) and the app log tail of `EventStreamGateway` in provisioning-svc. The `registerTool` loops fill `via: 'mcp_tool'` and `tool`. The CLI
*   interceptor fills `method` and `route`.
* - The ticket registry of the DP telemetry broker in provisioning-svc fills the ticket fields of
*   the `dp_telemetry.ticket_open` and `dp_telemetry.ticket_close` rows (plan
*   `2026-10-27-dp-telemetry-residency`, WS-12, rulings R-021 and R-045 item 6).
*
* What it never holds:
*
* - The LogQL text or the free-text `search`. The redactor reads keys only, so a secret typed into
*   a search would survive it. The row keeps {@link readAuditSearchFingerprint} instead.
* - A log line.
* - A key that `isCpAuditSecretKey` marks secret, because the redactor would replace its value
*   (R-008).
* - A trust decision. `client` is the caller's claim, a label only. `via`, `authKind`, `jti` and
*   `azp` come from the server and the verified token, and they are the trusted part.
*
* Every query field accepts each value the DP log query accepts (`dpLogsQueryRequestSchema`), and
* each pod name up to 253 characters (R-007). The app filter fields `fixtureId`, `versionSlug`,
* `level` and `container` accept each value the app log query (`appLogsQuerySchema`) or the fleet
* log search (`appFleetLogsQuerySchema`) accepts (R-023). Through {@link fitReadAuditDetails},
* `start`, `end` and `cursor` accept each value the app or fleet log query accepts (R-020d,
* R-026): it cuts a `cursor` over 1,024 characters and an ISO-8601 instant over 64 characters, with
* `Z` or an offset, and marks each cut. It also replaces each control character of the free-text
* fields with U+FFFD (R-030b). The schema refuses an object over the 16 KB cap of the ingest schema
* (`CP_AUDIT_DETAILS_MAX_BYTES`), and {@link fitReadAuditDetails} keeps a row under it. The schema
* has refinements and no transform.
*/
/** How the read reached the service: an HTTP route, or a tool of a CP MCP server. */
const READ_AUDIT_VIA = ["route", "mcp_tool"];
/**
* The credential class of the caller, the value of `authKind`, read from the verified token
* (`request.zionVerifiedCpJwt`):
*
* - `first_party`: an operator login of the console or the CLI. `sessionType` is absent or `user`,
*   and `azp` is absent or empty.
* - `mcp_token`: an MCP agent token. `sessionType` is `mcp`, and `jti` names the token.
* - `oauth_client`: a token an OAuth client got for the operator. `sessionType` is absent or
*   `user`, and a non-empty `azp` names the client.
* - `other`: a `svc:` subject, whatever the token holds (R-016b), or a token whose `sessionType` is
*   set and is neither `user` nor `mcp`, such as a `service`, `agent`, `worker` or `external`
*   principal (R-013d). It is never read as a login.
*/
const READ_AUDIT_AUTH_KINDS = [
	"first_party",
	"mcp_token",
	"oauth_client",
	"other"
];
/** The HTTP methods a `cli.request` row may name. */
const READ_AUDIT_METHODS = [
	"GET",
	"POST",
	"PUT",
	"PATCH",
	"DELETE"
];
/** The number of hex characters of the search fingerprint: the first 8 bytes of a SHA-256. */
const READ_AUDIT_SEARCH_HASH_LENGTH = 16;
/** The most pod names one row records: the most the DP log query accepts. */
const READ_AUDIT_MAX_PODS = 100;
/** The most level names one row records: the most the DP log query accepts. */
const READ_AUDIT_MAX_LEVELS = 20;
/**
* The DP telemetry stores a ticket row may name, the value of `store`: the same three names, in the
* same order, as `TELEMETRY_STORES` of `@zion/sdk-cp-dp-contracts`. That package imports this one,
* so this package cannot import the list. A spec of `@zion/sdk-cp-dp-contracts` holds the two
* lists equal.
*/
const READ_AUDIT_TELEMETRY_STORES = [
	"prom",
	"tempo",
	"loki"
];
/**
* Why the broker admitted the read that opened a ticket, the value of `basis` (R-015):
*
* - `site`: the operator's live site permissions hold `cp:dp:telemetry-read` (R-005, R-030).
* - `grant`: a live grant on the data plane holds it (R-018).
*/
const READ_AUDIT_TELEMETRY_BASES = ["site", "grant"];
/**
* How the caller sent the operator's bearer to the broker, the value of `credentialKind`:
*
* - `bearer`: an `Authorization` header (site-admin-api, the CLI, an MCP server).
* - `cookie`: the Grafana `cp_token` cookie, which the broker turned into a bearer.
*
* The key is `credentialKind` and never `credential`: the redactor marks `credential` secret and
* would replace the value (R-008).
*/
const READ_AUDIT_TELEMETRY_CREDENTIAL_KINDS = ["bearer", "cookie"];
/**
* Why a ticket closed, the value of `reason` on a close row:
*
* - `expired`: the ticket reached its `expiresAt`.
* - `denied`: a later read of the same caller got the 403 of the per-DP decision. That read adds
*   to no counter of the close row.
* - `shutdown`: the broker module stopped with the ticket still live.
*/
const READ_AUDIT_TELEMETRY_CLOSE_REASONS = [
	"expired",
	"denied",
	"shutdown"
];
/**
* A UTC instant of a ticket row, the form `Date.prototype.toISOString` writes: exactly three
* fraction digits and `Z`, 24 characters. An offset, another precision or a date alone fails.
*/
const ticketInstant = z.iso.datetime({ precision: 3 });
/** The fields of a ticket open row, all or none. */
const TICKET_OPEN_FIELDS = [
	"basis",
	"expiresAt",
	"credentialKind"
];
/** The fields of a ticket close row, all or none. */
const TICKET_CLOSE_FIELDS = [
	"reason",
	"requests",
	"bytes",
	"refused",
	"openedAt"
];
/**
* The longest `cursor` a row keeps, in UTF-16 code units. {@link fitReadAuditDetails} cuts a longer
* cursor to it and sets `cursorTruncated`. With the widest filters a cursor of this length in the
* worst case (six JSON bytes per unit) can still pass the 16 KB cap, so step 3 of the fit cuts it
* further (R-023).
*/
const READ_AUDIT_CURSOR_MAX_LENGTH = 1024;
/** The hash of a fingerprint: the first 16 hex characters of a SHA-256, in lower case. */
const fingerprintHash = codeUnitString().length(16).regex(/^[0-9a-f]+$/);
/** An MCP tool name, the grammar of every CP MCP registry: snake case, at most 64 characters. */
const toolName = codeUnitString().min(1).max(64).regex(/^[a-z][a-z0-9_]*$/);
/**
* The fields of a read-audit `details` object, strict, with no cross-field rule and no size rule.
* {@link readAuditDetailsSchema} adds both.
*
* Fields, all optional unless named:
*
* - Query fields, as the caller sent them after validation: `namespace` (a DNS-1123 label or empty),
*   `pods` (at most 100 pod names of up to 253 characters), `container` (any text of up to 253
*   characters, the fleet search rule, which holds every CP pod name and every app query container,
*   R-023), `levels` (at most 20, each 1 to 16 characters on one line), `start` and `end` (the instant text, at most 64
*   characters), `limit`, `previous`, `tailLines`.
* - App log query fields (R-013c). `fixtureId`, `versionSlug` (each up to 253 characters) and `level`
*   (up to 64) take the rule of `appFleetLogsQuerySchema`, which holds each value of
*   `appLogsQuerySchema` too, the empty text included (R-023). `pod` and `direction` (`forward` or
*   `backward`) take the rule of `appLogsQuerySchema`. `cursor` (the opaque
*   page cursor, here 1 to 1,024 characters, R-020d), and `mode`, the aggregate of the query
*   (`count` or `count_by_level`; absent for a read of lines).
* - `startTruncated`, `endTruncated` and `cursorTruncated`: `true` when {@link fitReadAuditDetails}
*   cut that field. Each needs its field.
* - `podsTruncated` and `podCount`: both or neither. {@link fitReadAuditDetails} sets them when it
*   kept only the leading pods; `podCount` is the number of pods the caller asked for.
* - Target ids: `dataPlaneId` and `appId`, the ids of the data plane and the app a read names, in
*   the 8-4-4-4-12 hex shape of `z.guid()` with no RFC version rule (R-015).
* - Result fields: `linesReturned` (a count, absent when the read failed) and `truncated`.
* - `searchLength` and `searchHash`: both or neither, from {@link readAuditSearchFingerprint}.
* - `queryHash`: the 16 hex characters of a `cli.request` row, the fingerprint hash of the
*   canonical JSON of the request query (R-011b). A row keeps a query hash here and never in
*   `searchHash`, so a search for a typed text never matches a query.
* - `via` (required): `route` or `mcp_tool`. `tool` is required with `mcp_tool` and refused with
*   `route`.
* - `authKind` (required): one of {@link READ_AUDIT_AUTH_KINDS}. `jti` and `azp` come from the
*   verified token, 1 to 200 characters each on one line.
* - `client`: the claimed value of `x-zion-client` that `parseZionClient` accepted, unchanged.
* - `method` and `route`: both or neither. A `cli.request` row names the HTTP method and the
*   route template (`/v1/admin/data-planes/:id/k8s/pods`), never the concrete URL.
* - Ticket fields of the DP telemetry broker (WS-12, R-021, R-045 item 6):
*   - `ticketId`: the opaque ticket id, a lower-case UUID (`telemetryAccessIdSchema`), and `store`:
*     one of {@link READ_AUDIT_TELEMETRY_STORES}. Both or neither.
*   - Open fields, on a `dp_telemetry.ticket_open` row: `basis` (one of
*     {@link READ_AUDIT_TELEMETRY_BASES}), `expiresAt` (the ticket end) and `credentialKind` (one
*     of {@link READ_AUDIT_TELEMETRY_CREDENTIAL_KINDS}).
*   - Close fields, on a `dp_telemetry.ticket_close` row: `reason` (one of
*     {@link READ_AUDIT_TELEMETRY_CLOSE_REASONS}), `requests`, `bytes` (the answer bytes the
*     ticket passed), `refused` and `openedAt` (the ticket start). The three counts are safe
*     integers of 0 or more.
*   - `requests` counts every read that the broker counted on the ticket, served or refused.
*     `refused` counts the counted reads that got a refusal, so it is a part of `requests`. This
*     is the `count` outcome of the ticket registry (section 3.3 of `design-docs/WS-12.md` in plan
*     `2026-10-27-dp-telemetry-residency`): each counted read adds one to `requests`, and one to
*     `refused` when its outcome is a refusal.
*   - A read that the per-DP decision refuses with 403 never reaches the ticket count. It closes
*     the ticket `denied` and adds to no counter. So a ticket that served no read and then met a
*     revoked grant closes with `requests` 0 and `refused` 0.
*   - `expiresAt` and `openedAt` take the `toISOString` form: three fraction digits and `Z`.
*
* Any other key fails the parse.
*/
const readAuditDetailsFieldsSchema = z.object({
	namespace: dpLogsNamespaceSchema.optional(),
	pods: z.array(cpPodNameSchema).max(READ_AUDIT_MAX_PODS).optional(),
	podsTruncated: z.literal(true).optional(),
	podCount: z.number().int().positive().max(READ_AUDIT_MAX_PODS).optional(),
	container: appFleetLogsQuerySchema.shape.container,
	levels: z.array(dpLogsLevelSchema).max(READ_AUDIT_MAX_LEVELS).optional(),
	start: dpLogsInstantSchema.optional(),
	startTruncated: z.literal(true).optional(),
	end: dpLogsInstantSchema.optional(),
	endTruncated: z.literal(true).optional(),
	limit: z.number().int().positive().max(1e5).optional(),
	previous: z.boolean().optional(),
	tailLines: z.number().int().positive().max(1e5).optional(),
	fixtureId: appFleetLogsQuerySchema.shape.fixtureId,
	versionSlug: appFleetLogsQuerySchema.shape.versionSlug,
	pod: appLogsQuerySchema.shape.pod,
	level: appFleetLogsQuerySchema.shape.level,
	direction: appLogsQuerySchema.shape.direction,
	mode: appLogsQuerySchema.shape.aggregate,
	cursor: appLogsQuerySchema.shape.cursor.unwrap().max(READ_AUDIT_CURSOR_MAX_LENGTH).optional(),
	cursorTruncated: z.literal(true).optional(),
	dataPlaneId: z.guid().optional(),
	appId: z.guid().optional(),
	linesReturned: z.number().int().nonnegative().optional(),
	truncated: z.boolean().optional(),
	searchLength: z.number().int().nonnegative().optional(),
	searchHash: fingerprintHash.optional(),
	queryHash: fingerprintHash.optional(),
	via: z.enum(READ_AUDIT_VIA),
	tool: toolName.optional(),
	authKind: z.enum(READ_AUDIT_AUTH_KINDS),
	jti: singleLineText(200).optional(),
	azp: singleLineText(200).optional(),
	client: codeUnitString().max(64).regex(ZION_CLIENT_PATTERN).optional(),
	method: z.enum(READ_AUDIT_METHODS).optional(),
	route: codeUnitString().min(1).max(256).regex(/^\/[A-Za-z0-9._:/-]*$/).optional(),
	ticketId: telemetryAccessIdSchema.optional(),
	store: z.enum(READ_AUDIT_TELEMETRY_STORES).optional(),
	basis: z.enum(READ_AUDIT_TELEMETRY_BASES).optional(),
	expiresAt: ticketInstant.optional(),
	credentialKind: z.enum(READ_AUDIT_TELEMETRY_CREDENTIAL_KINDS).optional(),
	reason: z.enum(READ_AUDIT_TELEMETRY_CLOSE_REASONS).optional(),
	requests: z.number().int().nonnegative().optional(),
	bytes: z.number().int().nonnegative().optional(),
	refused: z.number().int().nonnegative().optional(),
	openedAt: ticketInstant.optional()
}).strict();
/** Each cut marker of {@link fitReadAuditDetails} and the field it marks. */
const CUT_MARKERS = [
	["startTruncated", "start"],
	["endTruncated", "end"],
	["cursorTruncated", "cursor"]
];
/**
* Add one issue per broken ticket rule of {@link readAuditDetailsSchema} to `ctx`. A row with no
* ticket field gets no issue. Pure apart from `ctx`.
*/
function checkTicketFields(details, ctx) {
	if (details.ticketId === void 0 !== (details.store === void 0)) ctx.addIssue({
		code: "custom",
		path: ["store"],
		message: "ticketId and store go together"
	});
	const groups = [TICKET_OPEN_FIELDS, TICKET_CLOSE_FIELDS].map((fields) => ({
		fields,
		present: fields.filter((field) => details[field] !== void 0)
	}));
	for (const { fields, present } of groups) {
		const missing = fields.find((field) => details[field] === void 0);
		if (present.length > 0 && missing !== void 0) ctx.addIssue({
			code: "custom",
			path: [missing],
			message: `${fields.join(", ")} go together`
		});
	}
	const [open, close] = groups.map((group) => group.present.length > 0);
	if (open && close) ctx.addIssue({
		code: "custom",
		path: ["ticketId"],
		message: "a ticket row holds the open fields or the close fields, never both"
	});
	if (details.ticketId !== void 0 && !open && !close) ctx.addIssue({
		code: "custom",
		path: ["ticketId"],
		message: "ticketId needs the open fields or the close fields"
	});
	if (details.ticketId === void 0 && (open || close)) ctx.addIssue({
		code: "custom",
		path: ["ticketId"],
		message: "the open fields and the close fields need ticketId"
	});
	if (details.refused !== void 0 && details.requests !== void 0 && details.refused > details.requests) ctx.addIssue({
		code: "custom",
		path: ["refused"],
		message: "refused must be at most requests"
	});
}
/**
* The strict `details` object of a read-audit row: {@link readAuditDetailsFieldsSchema} plus these
* rules, each a parse failure with the path named:
*
* - `searchLength` and `searchHash` go together; `method` and `route` go together.
* - `tool` is required when `via` is `mcp_tool` and refused when `via` is `route`.
* - `podsTruncated` and `podCount` go together, and `podCount` is more than the pods kept.
* - `startTruncated`, `endTruncated` and `cursorTruncated` each need their field.
* - Ticket rules (F3 of the plan's defaults: refuse over accept):
*   - `ticketId` and `store` go together.
*   - The open fields go together, and the close fields go together. The issue names the first
*     missing field.
*   - A row holds the open fields or the close fields, never both.
*   - `ticketId` needs one of the two groups, and each group needs `ticketId`.
*   - `refused` is at most `requests`, because each refused read is also a counted request.
* - The UTF-8 JSON of the object is at most `CP_AUDIT_DETAILS_MAX_BYTES` (16,384) bytes, the same
*   measure the ingest schema applies.
*
* The rules do not bind a group to an action. The writer sends the open fields with
* `dp_telemetry.ticket_open` and the close fields with `dp_telemetry.ticket_close`.
*/
const readAuditDetailsSchema = readAuditDetailsFieldsSchema.superRefine((details, ctx) => {
	checkTicketFields(details, ctx);
	if (details.searchLength === void 0 !== (details.searchHash === void 0)) ctx.addIssue({
		code: "custom",
		path: ["searchHash"],
		message: "searchLength and searchHash go together"
	});
	if (details.via === "mcp_tool" && details.tool === void 0) ctx.addIssue({
		code: "custom",
		path: ["tool"],
		message: "tool is required when via is mcp_tool"
	});
	if (details.via === "route" && details.tool !== void 0) ctx.addIssue({
		code: "custom",
		path: ["tool"],
		message: "tool is refused when via is route"
	});
	if (details.method === void 0 !== (details.route === void 0)) ctx.addIssue({
		code: "custom",
		path: ["route"],
		message: "method and route go together"
	});
	if (details.podsTruncated === void 0 !== (details.podCount === void 0)) ctx.addIssue({
		code: "custom",
		path: ["podCount"],
		message: "podsTruncated and podCount go together"
	});
	else if (details.podCount !== void 0 && details.podCount <= (details.pods?.length ?? 0)) ctx.addIssue({
		code: "custom",
		path: ["podCount"],
		message: "podCount must be more than the pods kept"
	});
	for (const [marker, field] of CUT_MARKERS) if (details[marker] !== void 0 && details[field] === void 0) ctx.addIssue({
		code: "custom",
		path: [marker],
		message: `${marker} needs ${field}`
	});
	if (cpAuditJsonByteLength(details) > 16384) ctx.addIssue({
		code: "custom",
		path: [],
		message: `details must be at most ${CP_AUDIT_DETAILS_MAX_BYTES} bytes as JSON`
	});
});
/**
* An ISO-8601 instant whose fraction makes it longer than the bound: group 1 is the date, the time
* and the fraction, group 2 the zone, `Z` or an offset `+hh:mm` / `-hh:mm` (R-026).
*/
const LONG_ISO_INSTANT = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+)(Z|[+-]\d{2}:\d{2})$/;
/**
* An instant cut to {@link DP_LOGS_INSTANT_MAX_LENGTH}, or undefined when no cut applies. Only an
* ISO-8601 text with a fraction can pass the app or fleet log query at that length, so the cut
* drops fraction digits and keeps the zone: the result is still the same instant to the millisecond.
*/
function cutInstant(value) {
	if (value === void 0 || value.length <= 64) return void 0;
	const match = LONG_ISO_INSTANT.exec(value);
	if (match === null) return void 0;
	const zone = String(match[2]);
	return `${String(match[1]).slice(0, 64 - zone.length)}${zone}`;
}
/** The free-text fields whose control characters {@link fitReadAuditDetails} replaces (R-030b, R-032c). */
const FREE_TEXT_FIELDS = [
	"container",
	"fixtureId",
	"versionSlug",
	"level",
	"cursor",
	"pod"
];
/** Each control character (`\p{Cc}`: C0, DEL and C1) of a text. */
const CONTROL_CHARACTER = /\p{Cc}/gu;
/**
* The draft with each control character of a free-text field replaced by U+FFFD (R-030b). One code
* unit replaces one, so no length changes. A field that is absent, or not a text, stays as it is.
*/
function replaceControls(draft) {
	const replaced = {};
	for (const field of FREE_TEXT_FIELDS) {
		const value = draft[field];
		if (typeof value === "string") replaced[field] = value.replace(CONTROL_CHARACTER, "�");
	}
	return {
		...draft,
		...replaced
	};
}
/** The leading `length` code units of a text, one fewer when the cut would split a surrogate pair. */
function leadingUnits(value, length) {
	const last = value.charCodeAt(length - 1);
	const next = value.charCodeAt(length);
	const splitsPair = last >= 55296 && last <= 56319 && next >= 56320 && next <= 57343;
	return value.slice(0, splitsPair ? length - 1 : length);
}
/** A cursor cut to {@link READ_AUDIT_CURSOR_MAX_LENGTH}, never between the halves of a pair; else undefined. */
function cutCursor(value) {
	if (value === void 0 || value.length <= READ_AUDIT_CURSOR_MAX_LENGTH) return void 0;
	return leadingUnits(value, READ_AUDIT_CURSOR_MAX_LENGTH);
}
/**
* The row with the longest leading part of its cursor that keeps it under the cap, marked with
* `cursorTruncated`. A row with no cursor comes back as it is, and the parse then refuses its size.
*/
function cutCursorToFit(row) {
	const cursor = row.cursor;
	if (cursor === void 0) return row;
	const withCursor = (length) => ({
		...row,
		cursor: leadingUnits(cursor, length),
		cursorTruncated: true
	});
	let low = 1;
	let high = cursor.length;
	while (low < high) {
		const middle = Math.ceil((low + high) / 2);
		if (cpAuditJsonByteLength(withCursor(middle)) <= 16384) low = middle;
		else high = middle - 1;
	}
	return withCursor(low);
}
/** The draft with each over-long text field cut and marked. */
function cutTexts(draft) {
	const start = cutInstant(draft.start);
	const end = cutInstant(draft.end);
	const cursor = cutCursor(draft.cursor);
	return {
		...draft,
		...start === void 0 ? {} : {
			start,
			startTruncated: true
		},
		...end === void 0 ? {} : {
			end,
			endTruncated: true
		},
		...cursor === void 0 ? {} : {
			cursor,
			cursorTruncated: true
		}
	};
}
/**
* Keep a read-audit row under the 16 KB details cap, and parse it (R-007, R-020d).
*
* Input: a draft of the details. Output: the parsed details, after these steps.
* 0. Control characters (R-030b, R-032c). Each `\p{Cc}` character (C0, DEL and C1, such as an
*    escape, a new line or a bell) of `container`, `fixtureId`, `versionSlug`, `level`, `cursor`
*    and `pod` becomes
*    U+FFFD, so no row field can carry a terminal sequence or a false log line. The length stays.
* 1. Text cuts. A `cursor` over 1,024 UTF-16 code units keeps its first 1,024 (1,023 when the
*    1,024th and the 1,025th form a surrogate pair), and the output sets `cursorTruncated: true`.
*    A `start` or `end` over 64 characters in the ISO-8601 form `YYYY-MM-DDTHH:MM:SS.<digits>`
*    plus a zone (`Z`, or an offset `+hh:mm` or `-hh:mm`, R-026) drops fraction digits from the
*    end of the fraction until it is 64 characters, keeps the zone, and the output sets
*    `startTruncated` or `endTruncated`. The cut text is still a valid instant. A field at or
*    under its bound stays as it is.
* 2. When the result's UTF-8 JSON is at most `CP_AUDIT_DETAILS_MAX_BYTES`, the output equals it.
* 3. The cursor cut (R-023). When the result is larger, and still larger with no pods kept, the
*    output keeps the longest leading part of the `cursor` that fits (at least one code unit, never
*    half of a surrogate pair) and sets `cursorTruncated: true`.
* 4. The pod cut. When the result is larger and holds pods, the output keeps the longest leading
*    run of pods that fits after step 3, in order, sets `podsTruncated: true`, and sets `podCount` to
*    the number of pods in the draft. Every other field stays as the steps before left it.
*
* Why the cursor and the pods alone: with no pods and a cursor of one code unit, the field bounds
* hold every other field under the cap in the worst case (lone surrogates, which JSON writes as six
* bytes, and the widest fleet filters of R-023), so the row fits once both are cut. A row that
* every older rule accepted needs no cursor cut, so its output is the same as before R-023.
*
* Errors: it throws the `ZodError` of {@link readAuditDetailsSchema} when the draft breaks a field
* rule or a cross-field rule, such as a `start` over 64 characters in another form (an epoch, or an
* instant with no fraction). That is a
* defect of the caller. The size of the row is never a reason to throw. It is pure and synchronous.
*/
function fitReadAuditDetails(draft) {
	const cut = cutTexts(replaceControls(draft));
	if (cpAuditJsonByteLength(cut) <= 16384) return readAuditDetailsSchema.parse(cut);
	const pods = cut.pods ?? [];
	const noPods = pods.length === 0 ? cut : {
		...cut,
		pods: [],
		podsTruncated: true,
		podCount: pods.length
	};
	const base = cpAuditJsonByteLength(noPods) <= 16384 ? noPods : cutCursorToFit(noPods);
	if (pods.length === 0) return readAuditDetailsSchema.parse(base);
	const keep = (count) => ({
		...base,
		pods: pods.slice(0, count)
	});
	let low = 0;
	let high = pods.length - 1;
	while (low < high) {
		const middle = Math.ceil((low + high) / 2);
		if (cpAuditJsonByteLength(keep(middle)) <= 16384) low = middle;
		else high = middle - 1;
	}
	return readAuditDetailsSchema.parse(keep(low));
}
/**
* The fingerprint of a free-text search: its length in UTF-16 code units (`search.length`) and the
* first 16 hex characters of the SHA-256 of its UTF-8 bytes.
*
* Input: any string, also an empty one. Output: `{ searchLength, searchHash }`. Two equal searches
* give the same fingerprint, so a reader can tell repeats apart without the text. It is pure,
* synchronous and never throws.
*/
function readAuditSearchFingerprint(search) {
	return {
		searchLength: search.length,
		searchHash: bytesToHex(sha256(utf8ToBytes(search))).slice(0, 16)
	};
}
//#endregion
export { READ_AUDIT_AUTH_KINDS, READ_AUDIT_MAX_LEVELS, READ_AUDIT_MAX_PODS, READ_AUDIT_METHODS, READ_AUDIT_SEARCH_HASH_LENGTH, READ_AUDIT_TELEMETRY_BASES, READ_AUDIT_TELEMETRY_CLOSE_REASONS, READ_AUDIT_TELEMETRY_CREDENTIAL_KINDS, READ_AUDIT_TELEMETRY_STORES, READ_AUDIT_VIA, fitReadAuditDetails, readAuditDetailsFieldsSchema, readAuditDetailsSchema, readAuditSearchFingerprint };

//# sourceMappingURL=read-details.js.map